// Unsaved journal writing, held ONLY in this JavaScript module's memory (review fix 2026-09-10).
//
// Why memory and not storage: journal text is private reflection, and this app is used on shared
// devices. localStorage persists indefinitely, and even sessionStorage can be restored with a
// reopened tab -- either would leave someone's unsaved writing readable by the next person. A
// module-level variable survives in-app navigation (so leaving /journal and coming back, including
// with the browser Back button inside the app, restores the draft) and is gone the moment the tab
// is closed or reloaded. Reload/close is covered separately by a beforeunload warning (see
// hooks/useLeaveWarning.ts). Drafts are tagged with their owner's `sub` and cleared on logout and
// account deletion, so a different account signing in on the same tab never sees them.

interface DraftState {
  owner: string;
  newDraft: string;
  /** entryId -> unsaved edited text for that existing entry. */
  edits: Record<string, string>;
}

let drafts: DraftState | null = null;

function forOwner(owner: string): DraftState {
  if (!drafts || drafts.owner !== owner) drafts = { owner, newDraft: '', edits: {} };
  return drafts;
}

export function readJournalDrafts(owner: string): { newDraft: string; edits: Record<string, string> } {
  const d = forOwner(owner);
  return { newDraft: d.newDraft, edits: { ...d.edits } };
}

export function writeNewJournalDraft(owner: string, text: string): void {
  forOwner(owner).newDraft = text;
}

/** `text === null` discards the unsaved edit for that entry. */
export function writeJournalEditDraft(owner: string, entryId: string, text: string | null): void {
  const d = forOwner(owner);
  if (text === null) delete d.edits[entryId];
  else d.edits[entryId] = text;
}

export function clearJournalDrafts(): void {
  drafts = null;
}
